import api from '../../services/api';
import { logError } from '../../utils/logger';
import { DataValidators, TypeGuards } from '../../utils/validators';
import { authService } from '../authService'; // CORREÇÃO: Importar authService

// CORREÇÃO: Importar tipos centralizados, não redefinir
import type { 
  Consulta, 
  ConsultaInput, 
  ConsultaUpdate,
  FiltroConsultas
} from '../../types/consulta';

export const consultasService = {
  async listarTodas(filtros?: FiltroConsultas | string): Promise<Consulta[]> {
    try {
      let queryString = '';
      
      if (typeof filtros === 'string') {
        queryString = filtros.startsWith('?') ? filtros : `?${filtros}`;
      } else if (filtros && typeof filtros === 'object') {
        const params = new URLSearchParams();
        
        Object.entries(filtros).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
        
        const paramString = params.toString();
        queryString = paramString ? `?${paramString}` : '';
      }

      const response = await api.get<any[]>(`/consultas${queryString}`);
      
      const consultasValidadas = DataValidators.validateConsultas(response.data);
      
      // CORREÇÃO: Usar conversão simples e consistente
      return consultasValidadas.map(consulta => ({
        id: String(consulta.id),
        pacienteId: String(consulta.pacienteId),
        usuarioId: String(consulta.usuarioId),
        data: consulta.dataConsulta || consulta.data || '',
        hora: consulta.horaInicio || consulta.hora || '',
        horaFim: consulta.horaFim,
        valor: Number(consulta.valor) || 0,
        
        statusConsulta: consulta.statusConsulta || (consulta.realizada ? 'realizada' : 'agendada'),
        statusPagamento: consulta.statusPagamento || (consulta.pagamentoRecebido ? 'pago' : 'pendente'),
        dataPagamento: consulta.dataPagamento,
        formaPagamento: consulta.formaPagamento,
        observacoesAgendamento: consulta.observacoesAgendamento || consulta.observacoes,
        
        realizada: consulta.realizada ?? (consulta.statusConsulta === 'realizada'),
        pagamentoRecebido: consulta.pagamentoRecebido ?? (consulta.statusPagamento === 'pago'),
        
        dataConsulta: consulta.dataConsulta || consulta.data || '',
        horaInicio: consulta.horaInicio || consulta.hora || '',
        observacoes: consulta.observacoes || consulta.observacoesAgendamento,
        
        // ✅ CAMPOS DE CONFIRMAÇÃO
        confirmadaPaciente: consulta.confirmadaPaciente || false,
        dataConfirmacao: consulta.dataConfirmacao,
        tokenConfirmacao: consulta.tokenConfirmacao,
        tokenExpiracao: consulta.tokenExpiracao,
        
        nomePaciente: consulta.nomePaciente,
        nomeUsuario: consulta.nomeUsuario,
        createdAt: consulta.createdAt,
        updatedAt: consulta.updatedAt
      }));
    } catch (error) {
      console.error('Erro ao buscar consultas:', error);
      return [];
    }
  },
  
  async obterPorId(id: string): Promise<Consulta | null> {
    try {
      const response = await api.get<any>(`/consultas/${id}`);

      if (!response.data) {
        console.error('ConsultaService: Dados incompletos retornados da API', response.data);
        return null;
      }
      
      // CORREÇÃO: Usar conversão consistente
      const consulta = response.data;
      return {
        id: String(consulta.id),
        pacienteId: String(consulta.pacienteId),
        usuarioId: String(consulta.usuarioId),
        data: consulta.dataConsulta || consulta.data || '',
        hora: consulta.horaInicio || consulta.hora || '',
        horaFim: consulta.horaFim,
        valor: Number(consulta.valor) || 0,
        
        statusConsulta: consulta.statusConsulta || 'agendada',
        statusPagamento: consulta.statusPagamento || 'pendente',
        dataPagamento: consulta.dataPagamento,
        formaPagamento: consulta.formaPagamento,
        observacoesAgendamento: consulta.observacoesAgendamento || consulta.observacoes,
        
        realizada: consulta.realizada ?? (consulta.statusConsulta === 'realizada'),
        pagamentoRecebido: consulta.pagamentoRecebido ?? (consulta.statusPagamento === 'pago'),
        
        dataConsulta: consulta.dataConsulta || consulta.data || '',
        horaInicio: consulta.horaInicio || consulta.hora || '',
        observacoes: consulta.observacoes || consulta.observacoesAgendamento,
        
        // ✅ CAMPOS DE CONFIRMAÇÃO
        confirmadaPaciente: consulta.confirmadaPaciente || false,
        dataConfirmacao: consulta.dataConfirmacao,
        tokenConfirmacao: consulta.tokenConfirmacao,
        tokenExpiracao: consulta.tokenExpiracao,
        
        nomePaciente: consulta.nomePaciente,
        nomeUsuario: consulta.nomeUsuario,
        createdAt: consulta.createdAt,
        updatedAt: consulta.updatedAt
      };
    } catch (error) {
      logError('ConsultasService', `Erro ao obter consulta ${id}`, error);
      return null;
    }
  },

  async criar(consulta: ConsultaInput): Promise<Consulta> {
    try {
      if (!TypeGuards.isNonEmptyString(consulta.pacienteId)) {
        throw new Error('ID do paciente é obrigatório');
      }

      if (!TypeGuards.isValidDate(consulta.data)) {
        throw new Error('Data da consulta é obrigatória e deve ser válida');
      }

      if (!TypeGuards.isValidNumber(consulta.valor)) {
        throw new Error('Valor da consulta deve ser um número válido');
      }

      // CORREÇÃO: Obter usuário logado para usar seu ID
      const usuarioAtual = authService.getUsuario();
      if (!usuarioAtual) {
        throw new Error('Usuário não autenticado. Faça login novamente.');
      }

      // CORREÇÃO: Converter para formato do backend usando ID do usuário logado
      const consultaParaAPI = {
        pacienteId: consulta.pacienteId,
        usuarioId: String(usuarioAtual.id), // CORREÇÃO: Usar ID do usuário autenticado
        dataConsulta: consulta.data,
        horaInicio: consulta.hora,
        horaFim: consulta.horaFim,
        valor: DataValidators.safeNumber(consulta.valor),
        statusConsulta: consulta.statusConsulta || (consulta.realizada ? 'realizada' : 'agendada'),
        statusPagamento: consulta.statusPagamento || (consulta.pagamentoRecebido ? 'pago' : 'pendente'),
        dataPagamento: consulta.dataPagamento,
        formaPagamento: consulta.formaPagamento,
        observacoesAgendamento: consulta.observacoes
      };

      const response = await api.post<any>('/consultas', consultaParaAPI);
      
      // CORREÇÃO: Usar conversão consistente na resposta
      const consultaCriada = response.data;
      return {
        id: String(consultaCriada.id),
        pacienteId: String(consultaCriada.pacienteId),
        usuarioId: String(consultaCriada.usuarioId),
        data: consultaCriada.dataConsulta || consultaCriada.data || '',
        hora: consultaCriada.horaInicio || consultaCriada.hora || '',
        horaFim: consultaCriada.horaFim,
        valor: Number(consultaCriada.valor) || 0,
        
        statusConsulta: consultaCriada.statusConsulta || 'agendada',
        statusPagamento: consultaCriada.statusPagamento || 'pendente',
        dataPagamento: consultaCriada.dataPagamento,
        formaPagamento: consultaCriada.formaPagamento,
        observacoesAgendamento: consultaCriada.observacoesAgendamento || consultaCriada.observacoes,
        
        realizada: consultaCriada.realizada ?? (consultaCriada.statusConsulta === 'realizada'),
        pagamentoRecebido: consultaCriada.pagamentoRecebido ?? (consultaCriada.statusPagamento === 'pago'),
        
        dataConsulta: consultaCriada.dataConsulta || consultaCriada.data || '',
        horaInicio: consultaCriada.horaInicio || consultaCriada.hora || '',
        observacoes: consultaCriada.observacoes || consultaCriada.observacoesAgendamento,
        
        nomePaciente: consultaCriada.nomePaciente,
        nomeUsuario: consultaCriada.nomeUsuario,
        createdAt: consultaCriada.createdAt,
        updatedAt: consultaCriada.updatedAt
      };
    } catch (error) {
      console.error('Erro ao criar consulta:', error);
      throw error;
    }
  },

  async atualizar(id: string, consulta: ConsultaUpdate): Promise<Consulta> {
    try {

      const consultaParaAPI: any = { ...consulta };
      
      if (consulta.pagamentoRecebido !== undefined) {
        consultaParaAPI.statusPagamento = consulta.pagamentoRecebido ? 'pago' : 'pendente';
        delete consultaParaAPI.pagamentoRecebido;
      }
      
      if (consulta.realizada !== undefined) {
        consultaParaAPI.statusConsulta = consulta.realizada ? 'realizada' : 'agendada';
        delete consultaParaAPI.realizada;
      }

      const response = await api.put<any>(`/consultas/${id}`, consultaParaAPI);
      
      // CORREÇÃO: Usar conversão consistente
      const consultaAtualizada = response.data;
      return {
        id: String(consultaAtualizada.id),
        pacienteId: String(consultaAtualizada.pacienteId),
        usuarioId: String(consultaAtualizada.usuarioId),
        data: consultaAtualizada.dataConsulta || consultaAtualizada.data || '',
        hora: consultaAtualizada.horaInicio || consultaAtualizada.hora || '',
        horaFim: consultaAtualizada.horaFim,
        valor: Number(consultaAtualizada.valor) || 0,
        
        statusConsulta: consultaAtualizada.statusConsulta || 'agendada',
        statusPagamento: consultaAtualizada.statusPagamento || 'pendente',
        dataPagamento: consultaAtualizada.dataPagamento,
        formaPagamento: consultaAtualizada.formaPagamento,
        observacoesAgendamento: consultaAtualizada.observacoesAgendamento || consultaAtualizada.observacoes,
        
        realizada: consultaAtualizada.realizada ?? (consultaAtualizada.statusConsulta === 'realizada'),
        pagamentoRecebido: consultaAtualizada.pagamentoRecebido ?? (consultaAtualizada.statusPagamento === 'pago'),
        
        dataConsulta: consultaAtualizada.dataConsulta || consultaAtualizada.data || '',
        horaInicio: consultaAtualizada.horaInicio || consultaAtualizada.hora || '',
        observacoes: consultaAtualizada.observacoes || consultaAtualizada.observacoesAgendamento,
        
        nomePaciente: consultaAtualizada.nomePaciente,
        nomeUsuario: consultaAtualizada.nomeUsuario,
        createdAt: consultaAtualizada.createdAt,
        updatedAt: consultaAtualizada.updatedAt
      };
    } catch (error) {
      logError('ConsultasService', `Erro ao atualizar consulta ${id}`, error);
      throw error;
    }
  },

  async excluir(id: string): Promise<boolean> {
    try {
      await api.delete(`/consultas/${id}`);
      return true;
    } catch (error) {
      logError('ConsultasService', `Erro ao excluir consulta ${id}`, error);
      throw error;
    }
  },
  
  async marcarRealizada(id: string, pagamentoRecebido: boolean = false): Promise<Consulta> {
    try {
      const response = await api.put<Consulta>(`/consultas/${id}/realizar`, { pagamentoRecebido });
      return response.data;
    } catch (error) {
      logError('ConsultasService', `Erro ao marcar consulta ${id} como realizada`, error);
      throw error;
    }
  },
  
  async cancelar(id: string, motivo: string = 'Cancelada pelo usuário'): Promise<Consulta> {
    try {
      try {
        const response = await api.put<Consulta>(`/consultas/${id}/cancelar`, { 
          motivoCancelamento: motivo 
        });
        return response.data;
      } catch (endpointError) {
        return await this.atualizar(id, {
          statusConsulta: 'cancelada',
          statusPagamento: 'cancelado'
        });
      }
    } catch (error) {
      logError('ConsultasService', `Erro ao cancelar consulta ${id}`, error);
      throw error;
    }
  },

  async excluirDefinitivamente(id: string): Promise<boolean> {
    try {
      await api.delete(`/consultas/${id}`);
      return true;
    } catch (error) {
      logError('ConsultasService', `Erro ao excluir definitivamente consulta ${id}`, error);
      throw error;
    }
  },

  /**
   * Valida o agendamento verificando conflitos e disponibilidade
   */
  async validarAgendamento(dados: {
    usuarioId: string;
    dataConsulta: string;
    horaInicio: string;
    horaFim?: string;
    consultaIdExcluir?: string;
  }): Promise<{
    sucesso: boolean;
    valido: boolean;
    tipo?: 'conflito' | 'indisponivel';
    mensagem: string;
    consultaConflitante?: {
      id: string;
      pacienteNome: string;
      horaInicio: string;
      horaFim: string | null;
    };
    detalhes?: {
      diaAtivo: boolean;
      horarioTrabalho?: { inicio: string; fim: string };
      intervalos?: { inicio: string; fim: string; observacoes?: string }[];
    };
  }> {
    try {
      const response = await api.post('/consultas/validar/agendamento', dados);
      return response.data;
    } catch (error) {
      logError('ConsultasService', 'Erro ao validar agendamento', error);
      return {
        sucesso: false,
        valido: false,
        mensagem: 'Erro ao validar agendamento. Tente novamente.'
      };
    }
  },

  /**
   * Verifica conflito de horário com outras consultas
   */
  async verificarConflitoHorario(dados: {
    usuarioId: string;
    dataConsulta: string;
    horaInicio: string;
    horaFim?: string;
    consultaIdExcluir?: string;
  }): Promise<{
    sucesso: boolean;
    temConflito: boolean;
    consultaConflitante?: {
      id: string;
      pacienteNome: string;
      horaInicio: string;
      horaFim: string | null;
    };
  }> {
    try {
      const response = await api.post('/consultas/validar/conflito-horario', dados);
      return response.data;
    } catch (error) {
      logError('ConsultasService', 'Erro ao verificar conflito', error);
      return {
        sucesso: false,
        temConflito: false
      };
    }
  },

  /**
   * Verifica disponibilidade do psicólogo
   */
  async verificarDisponibilidade(dados: {
    usuarioId: string;
    dataConsulta: string;
    horaInicio: string;
    horaFim?: string;
  }): Promise<{
    sucesso: boolean;
    disponivel: boolean;
    motivo?: string;
    detalhes?: {
      diaAtivo: boolean;
      horarioTrabalho?: { inicio: string; fim: string };
      intervalos?: { inicio: string; fim: string; observacoes?: string }[];
    };
  }> {
    try {
      const response = await api.post('/consultas/validar/disponibilidade', dados);
      return response.data;
    } catch (error) {
      logError('ConsultasService', 'Erro ao verificar disponibilidade', error);
      return {
        sucesso: false,
        disponivel: true // Em caso de erro, permitir agendamento
      };
    }
  }
};
